/**
 * Drawing ink onto a canvas layer.
 *
 * Width comes from two things: the stroke's canvas-space width (SRS C3, frozen) and
 * the pressure recorded at each point. Both live in the stroke record, so a stroke
 * drawn on a tablet and one drawn with a mouse render through exactly this same path.
 *
 * The highlighter is the exception, and deliberately so. SRS C4 gives it a flat width,
 * which lets the whole line be drawn as one path in one go. That is not just a
 * simplification: a see-through line built from many overlapping pieces darkens at
 * every join, and a single path has no joins to darken.
 */

import {
  canvasToScreen,
  canvasWidthToScreenWidth,
  clamp,
  MAX_RENDERED_WIDTH_PX,
  MIN_RENDERED_WIDTH_PX,
  type Point,
  type Stroke,
  type Viewport,
} from '@opal/shared';

/** A point as the input pipeline produces it: canvas units plus stored pressure. */
export interface InkPoint {
  readonly x: number;
  readonly y: number;
  readonly pressure: number;
}

/** How far pressure is allowed to thin or thicken a line, either side of its width. */
export const MIN_PRESSURE_FACTOR = 0.35;
export const MAX_PRESSURE_FACTOR = 1.5;

/** Multiplier a recorded pressure applies to the stroke's width. */
export function pressureFactor(pressure: number): number {
  const share = clamp(pressure, 0, 1);
  return MIN_PRESSURE_FACTOR + share * (MAX_PRESSURE_FACTOR - MIN_PRESSURE_FACTOR);
}

/** The screen width a stroke draws at before pressure, clamped as SRS C3 requires. */
export function flatWidth(viewport: Viewport, canvasWidth: number): number {
  return clamp(
    canvasWidthToScreenWidth(viewport, canvasWidth),
    MIN_RENDERED_WIDTH_PX,
    MAX_RENDERED_WIDTH_PX,
  );
}

/** The screen width to stroke one point at, clamped exactly as SRS C3 requires. */
export function widthAt(viewport: Viewport, canvasWidth: number, pressure: number): number {
  const base = canvasWidthToScreenWidth(viewport, canvasWidth);
  return clamp(
    base * pressureFactor(pressure),
    MIN_RENDERED_WIDTH_PX,
    MAX_RENDERED_WIDTH_PX,
  );
}

/** Wipes a layer, ignoring the pixel-ratio transform that is set on its context. */
export function clearCanvas(ctx: CanvasRenderingContext2D): void {
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.restore();
}

/** Reads a stroke's flat x, y, pressure triplets back into points. */
export function strokePoints(stroke: Stroke): InkPoint[] {
  const points: InkPoint[] = [];
  for (let index = 0; index + 2 < stroke.points.length; index += 3) {
    points.push({
      x: stroke.points[index] ?? 0,
      y: stroke.points[index + 1] ?? 0,
      pressure: stroke.points[index + 2] ?? 0,
    });
  }
  return points;
}

export interface InkStyle {
  readonly color: string;
  readonly opacity: number;
  readonly canvasWidth: number;
  /** SRS C4 — the highlighter's width does not follow pressure. */
  readonly flat?: boolean;
}

/** SRS C4 — only the highlighter draws flat. */
export function isFlatTool(tool: Stroke['tool']): boolean {
  return tool === 'highlighter';
}

/** The style a finished stroke renders with. */
export function styleOf(stroke: Stroke): InkStyle {
  return {
    color: stroke.color,
    opacity: stroke.opacity,
    canvasWidth: stroke.canvasWidth,
    flat: isFlatTool(stroke.tool),
  };
}

function middle(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/** A single sample: a round dot the width the pen would have drawn there. */
function drawDot(
  ctx: CanvasRenderingContext2D,
  point: InkPoint,
  style: InkStyle,
  viewport: Viewport,
): void {
  const at = canvasToScreen(viewport, point);
  const width = style.flat
    ? flatWidth(viewport, style.canvasWidth)
    : widthAt(viewport, style.canvasWidth, point.pressure);
  ctx.beginPath();
  ctx.arc(at.x, at.y, width / 2, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * Pressure-following ink: one short curve per sample, each stroked on its own so its
 * width can follow the pressure at that point. A single path could only carry one
 * width for the whole line.
 *
 * `from` is how a long stroke stays cheap: while the pen is down the board redraws
 * only the samples that arrived since the last frame, instead of thousands of old
 * ones. Passing 0 draws the whole stroke.
 */
function drawVariable(
  ctx: CanvasRenderingContext2D,
  points: readonly InkPoint[],
  style: InkStyle,
  viewport: Viewport,
  from: number,
): void {
  const at = (index: number): Point =>
    canvasToScreen(viewport, points[index] ?? { x: 0, y: 0 });

  for (let index = Math.max(1, from); index < points.length; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    if (!previous || !current) continue;

    const control = at(index - 1);
    const start = index === 1 ? control : middle(at(index - 2), control);
    const end = middle(control, at(index));

    ctx.beginPath();
    ctx.lineWidth = widthAt(viewport, style.canvasWidth, previous.pressure);
    ctx.moveTo(start.x, start.y);
    ctx.quadraticCurveTo(control.x, control.y, end.x, end.y);
    ctx.stroke();

    // The tip: from the last midpoint out to where the pen actually is, so the ink
    // reaches the nib instead of stopping half a sample behind it.
    if (index === points.length - 1) {
      const tip = at(index);
      ctx.beginPath();
      ctx.lineWidth = widthAt(viewport, style.canvasWidth, current.pressure);
      ctx.moveTo(end.x, end.y);
      ctx.lineTo(tip.x, tip.y);
      ctx.stroke();
    }
  }
}

/**
 * Flat ink: the whole line as one path, stroked once.
 *
 * This is what keeps the highlighter even. Painting it in pieces would lay 40% ink
 * over 40% ink at every join and leave a line of dark beads down the middle of it.
 */
function drawFlat(
  ctx: CanvasRenderingContext2D,
  points: readonly InkPoint[],
  style: InkStyle,
  viewport: Viewport,
): void {
  const at = (index: number): Point =>
    canvasToScreen(viewport, points[index] ?? { x: 0, y: 0 });

  ctx.beginPath();
  ctx.lineWidth = flatWidth(viewport, style.canvasWidth);
  const start = at(0);
  ctx.moveTo(start.x, start.y);
  for (let index = 1; index < points.length - 1; index += 1) {
    const control = at(index);
    const end = middle(control, at(index + 1));
    ctx.quadraticCurveTo(control.x, control.y, end.x, end.y);
  }
  const last = at(points.length - 1);
  ctx.lineTo(last.x, last.y);
  ctx.stroke();
}

/** Draws a stroke, or the newest part of one. */
export function drawInk(
  ctx: CanvasRenderingContext2D,
  points: readonly InkPoint[],
  style: InkStyle,
  viewport: Viewport,
  from = 0,
): void {
  const first = points[0];
  if (!first) return;

  ctx.save();
  ctx.strokeStyle = style.color;
  ctx.fillStyle = style.color;
  ctx.globalAlpha = style.opacity;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  if (points.length === 1) drawDot(ctx, first, style, viewport);
  else if (style.flat) drawFlat(ctx, points, style, viewport);
  else drawVariable(ctx, points, style, viewport, from);

  ctx.restore();
}

/** Draws a finished stroke from the board. */
export function drawStroke(
  ctx: CanvasRenderingContext2D,
  stroke: Stroke,
  viewport: Viewport,
): void {
  drawInk(ctx, strokePoints(stroke), styleOf(stroke), viewport);
}
